import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import BrandSymbol from '../ui/BrandSymbol';
import { validateEmail, sanitizeEmailInput } from '../../utils/emailValidation';
import { API_BASE_URL } from '../../config/api';
import { trackSignUp } from '../../utils/analytics';

const SOURCE_OPTIONS = [
  { value: '', label: 'Select an option...' },
  { value: 'google', label: 'Google Search' },
  { value: 'facebook', label: 'Facebook' },
  { value: 'instagram', label: 'Instagram' },
  { value: 'linkedin', label: 'LinkedIn' },
  { value: 'twitter', label: 'Twitter/X' },
  { value: 'friend', label: 'Friend or Family' },
  { value: 'advisor', label: 'Financial Advisor' },
  { value: 'attorney', label: 'Attorney' },
  { value: 'podcast', label: 'Podcast' },
  { value: 'blog', label: 'Blog or Article' },
  { value: 'advertisement', label: 'Online Advertisement' },
  { value: 'event', label: 'Event or Conference' },
  { value: 'other', label: 'Other' }
];

const SignupCapture: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Form state
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [source, setSource] = useState('');
  
  // UI state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  // UTM tracking
  const [utmParams, setUtmParams] = useState({
    utm_source: '',
    utm_medium: '',
    utm_campaign: ''
  });

  // Capture UTM params on mount
  useEffect(() => {
    setUtmParams({
      utm_source: searchParams.get('utm_source') || '',
      utm_medium: searchParams.get('utm_medium') || '',
      utm_campaign: searchParams.get('utm_campaign') || ''
    });
  }, [searchParams]);

  // Validation
  const emailValidation = validateEmail(email);
  const isComplete = firstName && lastName && email && emailValidation.isValid;

  const handleSubmit = async () => {
    if (!isComplete) return;

    setIsSubmitting(true);
    setError('');

    try {
      // Save lead to database
      const response = await fetch(`${API_BASE_URL}/api/leads/capture`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          firstName: firstName.trim(),
          lastName: lastName.trim(),
          email: email.toLowerCase().trim(),
          message: '',
          source,
          utmSource: utmParams.utm_source,
          utmMedium: utmParams.utm_medium,
          utmCampaign: utmParams.utm_campaign
        })
      });

      const result = await response.json();

      if (result.success) {
        trackSignUp({ method: 'email', source: source || utmParams.utm_source || 'direct' });
        // Encode data for Frame-129
        const redirectData = {
          e: email.toLowerCase().trim(),
          f: firstName.trim(),
          l: lastName.trim()
        };
        
        const encoded = btoa(JSON.stringify(redirectData));
        const urlSafe = encodeURIComponent(encoded);

        // Redirect to signup with pre-filled data
        navigate(`/frame-129?ref=${urlSafe}`);
      } else {
        setError(result.message || 'Something went wrong. Please try again.');
      }
    } catch (err) {
      console.error('[SignupCapture] Error:', err);
      setError('Failed to submit. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-cyan-50">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-6xl mx-auto px-4 py-4 flex justify-center">
          <BrandSymbol size="2xl" showText={true} variant="dark" />
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-6xl mx-auto px-4 py-12">
        <div className="grid md:grid-cols-2 gap-12 items-start">
          
          {/* Left Column - Content */}
          <div className="space-y-8">
            {/* Badge */}
            <div className="inline-block px-4 py-2 bg-cyan-100 text-cyan-800 rounded-full text-sm font-medium">
              Limited Beta Access
            </div>

            {/* Main Heading */}
            <div>
              <h1 className="text-4xl md:text-5xl font-bold text-slate-900 leading-tight">
                End-of-Life Organizer
              </h1>
              <p className="mt-4 text-lg text-slate-600 leading-relaxed">
                Thank you for your interest in joining our early access program for End of an Era.
              </p>
              <p className="mt-4 text-lg text-slate-600 leading-relaxed">
                The End-of-Life Organizer is your single, secure place for everything that matters. 
                Whether you're in your later years, supporting an aging parent, or preparing your own legacy, 
                the End-of-Life Organizer makes it simple to ensure nothing is lost.
              </p>
            </div>

            {/* Benefits Section */}
            <div className="bg-white rounded-xl shadow-sm p-6 border border-slate-100">
              <h2 className="text-xl font-semibold text-slate-900 mb-4">
                Benefits of Joining
              </h2>
              <p className="text-slate-600 mb-4">
                Shape the future of Estate Succession. As a beta user, you'll receive exclusive benefits 
                and have a direct impact on the development of the product.
              </p>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <span className="text-cyan-600 mt-1">✓</span>
                  <span className="text-slate-700">Early access to our secure digital vault, guided workflows, and sharing tools</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-cyan-600 mt-1">✓</span>
                  <span className="text-slate-700">A first look at how we help families plan, store documents, and capture important wishes</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-cyan-600 mt-1">✓</span>
                  <span className="text-slate-700">Direct input into the features we prioritize</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-cyan-600 mt-1">✓</span>
                  <span className="text-slate-700">Free access during the beta and lifetime discounts</span>
                </li>
              </ul>
            </div>

            {/* What to Expect Section */}
            <div className="bg-white rounded-xl shadow-sm p-6 border border-slate-100">
              <h2 className="text-xl font-semibold text-slate-900 mb-4">
                What to Expect
              </h2>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <span className="w-6 h-6 rounded-full bg-cyan-100 text-cyan-700 flex items-center justify-center text-sm font-medium flex-shrink-0">1</span>
                  <span className="text-slate-700">You'll be directed to the beta app login</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-6 h-6 rounded-full bg-cyan-100 text-cyan-700 flex items-center justify-center text-sm font-medium flex-shrink-0">2</span>
                  <span className="text-slate-700">Use and explore the platform</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-6 h-6 rounded-full bg-cyan-100 text-cyan-700 flex items-center justify-center text-sm font-medium flex-shrink-0">3</span>
                  <span className="text-slate-700">Add your real information with confidence. Your data is encrypted, protected with MFA, and secured on industry-standard infrastructure.</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-6 h-6 rounded-full bg-cyan-100 text-cyan-700 flex items-center justify-center text-sm font-medium flex-shrink-0">4</span>
                  <span className="text-slate-700">Share any feedback or ideas you have through a follow-up survey or 1:1 meeting</span>
                </li>
              </ul>
            </div>

            {/* Questions */}
            <p className="text-slate-600">
              Questions? Email: <a href="mailto:support@endofanera.ai" className="text-cyan-700 hover:underline font-medium">support@endofanera.ai</a>
            </p>
          </div>

          {/* Right Column - Form */}
          <div className="md:sticky md:top-8">
            <div className="bg-white rounded-xl shadow-lg p-8 border border-slate-100">
              <h2 className="text-2xl font-bold text-slate-900 mb-2">
                Register for Beta Access
              </h2>
              <p className="text-slate-600 mb-6">
                Complete the form below to join our early access program.
              </p>

              <div className="space-y-5">
                {/* First Name */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    First name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    className="w-full h-11 bg-white border border-slate-200 rounded-lg px-4 text-slate-800 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent transition-all"
                    placeholder="Enter your first name"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                  />
                </div>

                {/* Last Name */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Last name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    className="w-full h-11 bg-white border border-slate-200 rounded-lg px-4 text-slate-800 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent transition-all"
                    placeholder="Enter your last name"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                  />
                </div>

                {/* Email */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Email <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    className="w-full h-11 bg-white border border-slate-200 rounded-lg px-4 text-slate-800 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent transition-all"
                    placeholder="you@email.com"
                    value={email}
                    onChange={(e) => {
                      const sanitized = sanitizeEmailInput(e.target.value);
                      const normalized = sanitized.toLowerCase().trim();
                      setEmail(normalized);
                    }}
                  />
                  {email && !emailValidation.isValid && (
                    <p className="text-red-500 text-sm mt-1">{emailValidation.errors[0]}</p>
                  )}
                </div>

                {/* How did you hear about us - Optional */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    How did you hear about us? <span className="text-slate-400 font-normal">(optional)</span>
                  </label>
                  <select
                    className="w-full h-11 bg-white border border-slate-200 rounded-lg px-4 text-slate-800 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent transition-all"
                    value={source}
                    onChange={(e) => setSource(e.target.value)}
                  >
                    {SOURCE_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Error Message */}
                {error && (
                  <div className="text-red-600 text-sm bg-red-50 border border-red-200 rounded-lg px-4 py-3">
                    {error}
                  </div>
                )}

                {/* Submit Button */}
                <button
                  onClick={handleSubmit}
                  disabled={!isComplete || isSubmitting}
                  className={`w-full py-3 rounded-lg font-semibold text-white transition-all ${
                    isComplete && !isSubmitting
                      ? 'bg-cyan-700 hover:bg-cyan-800 cursor-pointer shadow-md hover:shadow-lg'
                      : 'bg-slate-300 cursor-not-allowed'
                  }`}
                >
                  {isSubmitting ? 'Signing up...' : 'Sign Up'}
                </button>

                {/* Privacy note */}
                <p className="text-xs text-slate-500 text-center">
                  By signing up, you agree to our{' '}
                  <a href="https://www.endofanera.ai/company/terms-of-service" target="_blank" rel="noopener noreferrer" className="text-cyan-700 hover:underline">
                    Terms of Service
                  </a>{' '}
                  and{' '}
                  <a href="https://www.endofanera.ai/company/privacy-policy" target="_blank" rel="noopener noreferrer" className="text-cyan-700 hover:underline">
                    Privacy Policy
                  </a>
                </p>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-white border-t border-slate-200 mt-16">
        <div className="max-w-6xl mx-auto px-4 py-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-sm text-slate-500">
              © 2025 End of an Era AI. All rights reserved
            </p>
            <div className="flex gap-6 text-sm text-slate-500">
              <a href="https://www.endofanera.ai/company/website-terms-of-use" target="_blank" rel="noopener noreferrer" className="hover:text-cyan-700">Terms of Use</a>
              <a href="https://www.endofanera.ai/company/terms-of-service" target="_blank" rel="noopener noreferrer" className="hover:text-cyan-700">Terms of Service</a>
              <a href="https://www.endofanera.ai/company/privacy-policy" target="_blank" rel="noopener noreferrer" className="hover:text-cyan-700">Privacy Policy</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default SignupCapture;
